import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router';
import { Surface, Button, Badge, Input, Select, Grid, StatCard, PageHeader } from '../../ui/components';
import { RefreshCw, Eye } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import FloatingOwenAssistant from '../../components/common/FloatingOwenAssistant';
import FloatingKnowledge from '../../components/common/FloatingKnowledge';

const getStatusColor = (status) => {
  const colors = {
    submitted_for_underwriting: 'warning',
    underwriting_in_progress: 'primary',
    conditionally_cleared: 'success',
    cleared_for_marketplace: 'success',
    needs_work: 'danger',
  };
  return colors[status] || 'neutral';
};

const getStatusLabel = (status) => {
  const labels = {
    submitted_for_underwriting: 'Pending Review',
    underwriting_in_progress: 'In Progress',
    conditionally_cleared: 'Cond. Cleared',
    cleared_for_marketplace: 'Cleared',
    needs_work: 'Needs Work',
  };
  return labels[status] || status;
};

const getScoreColor = (score) => {
  if (score >= 80) return 'success';
  if (score >= 60) return 'warning';
  return 'danger';
};

const UnderwriterPipeline = () => {
  const navigate = useNavigate();
  const { setPipelineCount } = useOutletContext() || {};
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchPipeline();
  }, []);

  useEffect(() => {
    filterApplications();
  }, [applications, searchTerm, statusFilter]);

  const fetchPipeline = async () => {
    setLoading(true);
    try {
      // In production, filter by underwriter_id
      const { data, error } = await supabase
        .from('loan_applications')
        .select('*')
        .in('status', ['submitted_for_underwriting', 'underwriting_in_progress', 'conditionally_cleared', 'cleared_for_marketplace', 'needs_work'])
        .order('created_at', { ascending: false });

      if (error) throw error;

      const pipelineData = data || [];
      setApplications(pipelineData);
      
      // Update pipeline count in layout
      if (setPipelineCount) {
        setPipelineCount(pipelineData.length);
      }
    } catch (error) {
      console.error('Error fetching pipeline:', error);
      // Use mock data for demo
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const filterApplications = () => {
    let filtered = applications;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(app => app.status === statusFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(app =>
        app.borrower_info?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.borrower_info?.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.borrower_info?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.anonymous_id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredApplications(filtered);
  };

  const handleViewApplication = (id) => {
    navigate(`/uw/application/${id}`);
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toLocaleString()}`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div>
      <PageHeader
        title="My Pipeline"
        description="Applications assigned to you"
        actions={<Button variant="primary" icon={RefreshCw} onClick={fetchPipeline}>Refresh</Button>}
      />

      {/* Stats Cards */}
      <Grid cols={4} gap="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-4)' }}>
        <StatCard label="Total in Pipeline" value={applications.length} />
        <StatCard label="In Progress" value={applications.filter(a => a.status === 'underwriting_in_progress').length} />
        <StatCard label="Cleared" value={applications.filter(a => ['conditionally_cleared', 'cleared_for_marketplace'].includes(a.status)).length} />
        <StatCard label="Needs Work" value={applications.filter(a => a.status === 'needs_work').length} />
      </Grid>

      {/* Filters */}
      <Surface padding="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-4)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 'var(--lk-space-3)', alignItems: 'center' }}>
          <Input
            placeholder="Search by borrower name, email, or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="submitted_for_underwriting">Pending Review</option>
            <option value="underwriting_in_progress">In Progress</option>
            <option value="conditionally_cleared">Conditionally Cleared</option>
            <option value="cleared_for_marketplace">Cleared</option>
            <option value="needs_work">Needs Work</option>
          </Select>
          <div style={{ textAlign: 'right', color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>
            Showing {filteredApplications.length} of {applications.length}
          </div>
        </div>
      </Surface>

      {/* Applications Table */}
      <Surface padding="0" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="lk-table">
            <thead>
              <tr>
                <th>Application ID</th>
                <th>Borrower</th>
                <th>Product</th>
                <th style={{ textAlign: 'right' }}>Loan Amount</th>
                <th style={{ textAlign: 'center' }}>Readiness</th>
                <th>Status</th>
                <th>Submitted</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: 'var(--lk-space-8)' }}>
                    <div className="lk-spinner lk-spinner-sm" style={{ display: 'inline-block', marginRight: 8 }} />
                    Loading pipeline...
                  </td>
                </tr>
              ) : filteredApplications.length > 0 ? (
                filteredApplications.map((app) => (
                  <tr key={app.id}>
                    <td style={{ fontFamily: 'monospace', fontWeight: 500 }}>
                      {app.anonymous_id || `LK-${app.id.slice(0, 8).toUpperCase()}`}
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--lk-primary-100)', color: 'var(--lk-primary-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--lk-text-xs)', fontWeight: 600, flexShrink: 0 }}>
                          {app.borrower_info?.first_name?.[0] || '?'}
                        </div>
                        <div>
                          <div style={{ fontWeight: 500 }}>
                            {app.borrower_info?.first_name || '-'} {app.borrower_info?.last_name || ''}
                          </div>
                          <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>{app.borrower_info?.email || '-'}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <Badge variant="neutral">
                        {app.product_type?.replace(/_/g, ' ') || '-'}
                      </Badge>
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(app.loan_amount)}</td>
                    <td style={{ textAlign: 'center' }}>
                      <Badge variant={getScoreColor(app.readiness_score || 0)}>
                        {app.readiness_score || 0}
                      </Badge>
                    </td>
                    <td>
                      <Badge variant={getStatusColor(app.status)}>
                        {getStatusLabel(app.status)}
                      </Badge>
                    </td>
                    <td style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>{formatDate(app.created_at)}</td>
                    <td style={{ textAlign: 'center' }}>
                      <Button size="sm" variant="ghost" icon={Eye} onClick={() => handleViewApplication(app.id)} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', color: 'var(--lk-text-quaternary)', padding: 'var(--lk-space-8)' }}>
                    {searchTerm || statusFilter !== 'all' 
                      ? 'No applications match your filters'
                      : 'Your pipeline is empty'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Surface>

      <FloatingOwenAssistant 
        context="underwriter-pipeline"
        contextData={{ applications: filteredApplications }}
      />
      <FloatingKnowledge 
        customContent={{
          label: 'Pipeline',
          title: 'Pipeline management',
          body: 'Manage your underwriting pipeline efficiently.',
          bullets: [
            'Prioritize applications by readiness score',
            'Track aging applications',
            'Monitor capacity utilization',
            'Clear conditions promptly',
          ],
          resources: [
            { label: 'Pipeline best practices', href: '#' },
            { label: 'Workflow optimization', href: '#' },
          ],
        }}
      />
    </div>
  );
};

export default UnderwriterPipeline;
